/**
 * Country Boundaries Layer Management
 *
 * Handles adding/updating Mapbox country boundary layers for:
 * - Visual glow effect based on content density
 * - Country selection highlighting
 * - Interactive hover states
 *
 * Uses Mapbox's built-in country-boundaries-v1 tileset with feature state
 * for dynamic styling based on film counts.
 */

import type mapboxgl from "mapbox-gl";
import { calculateCountryOpacity, countryNameToISO3, iso2ToISO3, iso3ToCountryName } from "../countryMapping";
import { MAPBOX_CONFIG } from "./config";

/**
 * Add country boundaries layers to the map
 *
 * Creates two layers:
 * 1. Fill layer - for country glow effect (opacity based on content density)
 * 2. Border layer - for subtle country outlines
 *
 * @param map - Mapbox GL map instance
 * @param countryCounts - Map of country names to film counts
 */
export function addCountryBoundaries(map: mapboxgl.Map, countryCounts: Map<string, number>): void {
  // Check if layers already exist (avoid duplicates)
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
    console.warn("Country boundaries layers already exist");
    return;
  }

  // Add Mapbox's country boundaries vector tileset as a source
  if (!map.getSource(MAPBOX_CONFIG.countryBoundaries.sourceId)) {
    map.addSource(MAPBOX_CONFIG.countryBoundaries.sourceId, {
      type: "vector",
      url: "mapbox://mapbox.country-boundaries-v1",
      promoteId: "iso_3166_1", // Use ISO3 code as feature ID for setFeatureState
    });
  }

  // Add fill layer for country glow effect
  map.addLayer({
    id: MAPBOX_CONFIG.countryBoundaries.fillLayerId,
    type: "fill",
    source: MAPBOX_CONFIG.countryBoundaries.sourceId,
    "source-layer": MAPBOX_CONFIG.countryBoundaries.sourceLayer,
    paint: {
      "fill-color": MAPBOX_CONFIG.countryBoundaries.fillColor,
      // Opacity driven by feature state (set based on content density)
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hasContent"], false],
        ["number", ["feature-state", "density"], 0],
        0, // Default: no opacity for countries without content
      ],
    },
  });

  // Add border layer for country outlines with hover effect
  map.addLayer({
    id: MAPBOX_CONFIG.countryBoundaries.borderLayerId,
    type: "line",
    source: MAPBOX_CONFIG.countryBoundaries.sourceId,
    "source-layer": MAPBOX_CONFIG.countryBoundaries.sourceLayer,
    paint: {
      // Dynamic color: accent on hover, default otherwise
      "line-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        MAPBOX_CONFIG.countryBoundaries.borderHoverColor,
        MAPBOX_CONFIG.countryBoundaries.borderColor,
      ],
      "line-width": MAPBOX_CONFIG.countryBoundaries.borderWidth,
      // Start transparent, will animate to visible on cursor enter
      "line-opacity": 0,
      // Smooth transition when opacity changes
      "line-opacity-transition": {
        duration: 800,
        delay: 0,
      },
    },
  });

  // Set feature state for each country based on content density
  updateCountryDensity(map, countryCounts);
}

/**
 * Update country density feature state based on film counts
 *
 * @param map - Mapbox GL map instance
 * @param countryCounts - Map of country names to film counts
 */
export function updateCountryDensity(map: mapboxgl.Map, countryCounts: Map<string, number>): void {
  if (!map.getSource(MAPBOX_CONFIG.countryBoundaries.sourceId)) {
    console.warn("Country boundaries source not found");
    return;
  }

  // Calculate max count for normalization
  const maxCount = Math.max(...Array.from(countryCounts.values()), 1);

  // Set feature state for each country with content
  for (const [countryName, count] of countryCounts) {
    const iso3 = countryNameToISO3(countryName);

    if (!iso3) {
      console.warn(`No ISO3 code found for country: ${countryName}`);
      continue;
    }

    const opacity = calculateCountryOpacity(count, maxCount);

    // Set feature state on the country polygon
    map.setFeatureState(
      {
        source: MAPBOX_CONFIG.countryBoundaries.sourceId,
        sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
        id: iso3,
      },
      {
        density: opacity,
        hasContent: true,
        filmCount: count,
      },
    );
  }
}

/**
 * Highlight a selected country
 *
 * @param map - Mapbox GL map instance
 * @param countryName - Name of country to highlight (null to clear)
 */
export function updateCountrySelection(map: mapboxgl.Map, countryName: string | null): void {
  if (!map.getSource(MAPBOX_CONFIG.countryBoundaries.sourceId)) {
    return;
  }

  // Selection visual feedback will be implemented in Phase 2.6
  // For now, just set feature state for future styling

  if (countryName) {
    const iso3 = countryNameToISO3(countryName);
    if (iso3) {
      map.setFeatureState(
        {
          source: MAPBOX_CONFIG.countryBoundaries.sourceId,
          sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
          id: iso3,
        },
        { selected: true },
      );
    }
  }
}

/**
 * Remove country boundaries layers from the map
 *
 * @param map - Mapbox GL map instance
 */
export function removeCountryBoundaries(map: mapboxgl.Map): void {
  // Remove layers
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId)) {
    map.removeLayer(MAPBOX_CONFIG.countryBoundaries.fillLayerId);
  }
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.borderLayerId)) {
    map.removeLayer(MAPBOX_CONFIG.countryBoundaries.borderLayerId);
  }

  // Remove source
  if (map.getSource(MAPBOX_CONFIG.countryBoundaries.sourceId)) {
    map.removeSource(MAPBOX_CONFIG.countryBoundaries.sourceId);
  }
}

/**
 * Show country borders with smooth transition animation
 *
 * @param map - Mapbox GL map instance
 */
export function showCountryBorders(map: mapboxgl.Map): void {
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.borderLayerId)) {
    map.setPaintProperty(MAPBOX_CONFIG.countryBoundaries.borderLayerId, "line-opacity", 0.6);
  }
}

/**
 * Hide country borders with smooth transition animation
 *
 * @param map - Mapbox GL map instance
 */
export function hideCountryBorders(map: mapboxgl.Map): void {
  if (map.getLayer(MAPBOX_CONFIG.countryBoundaries.borderLayerId)) {
    map.setPaintProperty(MAPBOX_CONFIG.countryBoundaries.borderLayerId, "line-opacity", 0);
  }
}

/**
 * Setup interactive hover effects for country boundaries
 * Changes cursor and highlights border on hover
 *
 * @param map - Mapbox GL map instance
 * @returns Cleanup function to remove event listeners
 */
export function setupCountryHover(map: mapboxgl.Map): () => void {
  let hoveredCountryId: string | null = null;

  const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const countryId = feature.properties?.iso_3166_1 as string;

      if (!countryId) return;

      // Change cursor to pointer
      map.getCanvas().style.cursor = "pointer";

      // If hovering over a new country, update feature state
      if (hoveredCountryId !== countryId) {
        // Clear previous hover state
        if (hoveredCountryId) {
          map.setFeatureState(
            {
              source: MAPBOX_CONFIG.countryBoundaries.sourceId,
              sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
              id: hoveredCountryId,
            },
            { hover: false },
          );
        }

        // Set new hover state
        hoveredCountryId = countryId;
        map.setFeatureState(
          {
            source: MAPBOX_CONFIG.countryBoundaries.sourceId,
            sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
            id: countryId,
          },
          { hover: true },
        );
      }
    }
  };
  const onMouseLeave = () => {
    // Reset cursor
    map.getCanvas().style.cursor = "";

    // Clear hover state
    if (hoveredCountryId) {
      map.setFeatureState(
        {
          source: MAPBOX_CONFIG.countryBoundaries.sourceId,
          sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
          id: hoveredCountryId,
        },
        { hover: false },
      );
      hoveredCountryId = null;
    }
  };

  // Add event listeners
  map.on("mousemove", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseMove);
  map.on("mouseleave", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseLeave);

  // Return cleanup function
  return () => {
    map.off("mousemove", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseMove);
    map.off("mouseleave", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseLeave);

    // Clear any remaining hover state
    if (hoveredCountryId) {
      map.setFeatureState(
        {
          source: MAPBOX_CONFIG.countryBoundaries.sourceId,
          sourceLayer: MAPBOX_CONFIG.countryBoundaries.sourceLayer,
          id: hoveredCountryId,
        },
        { hover: false },
      );
    }
  };
}

/**
 * Setup click handler for country selection
 *
 * @param map - Mapbox GL map instance
 * @param selectedCountry - Currently selected country (null if none)
 * @param onCountryClick - Callback when a country is clicked (null to deselect)
 * @returns Cleanup function to remove event listener
 */
export function setupCountryClick(
  map: mapboxgl.Map,
  selectedCountry: string | null,
  onCountryClick: (countryName: string | null) => void,
): () => void {
  const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const iso2Code = feature.properties?.iso_3166_1 as string;

      if (!iso2Code) {
        console.warn("[countryLayer] Country feature missing iso_3166_1 property");
        return;
      }

      // Convert ISO2 → ISO3 → Country Name
      // Mapbox's iso_3166_1 property contains ISO2 codes (e.g., "US", "FR")
      const iso3Code = iso2ToISO3(iso2Code);
      if (!iso3Code) {
        console.warn(`[countryLayer] No ISO3 mapping for ISO2 code: ${iso2Code}`);
        return;
      }

      const countryName = iso3ToCountryName(iso3Code);
      if (countryName) {
        // If clicking the already-selected country, deselect it
        if (selectedCountry === countryName) {
          onCountryClick(null);
        } else {
          onCountryClick(countryName);
        }
      } else {
        console.warn(`[countryLayer] No country name found for ISO3 code: ${iso3Code}`);
      }
    }
  };

  // Add click listener
  map.on("click", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onClick);

  // Return cleanup function
  return () => {
    map.off("click", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onClick);
  };
}

/**
 * Setup click handler for resetting country selection when clicking empty areas
 *
 * @param map - Mapbox GL map instance
 * @param onReset - Callback when user clicks outside any country (to reset filter)
 * @returns Cleanup function to remove event listener
 */
export function setupMapBackgroundClick(map: mapboxgl.Map, onReset: () => void): () => void {
  const onMapClick = (e: mapboxgl.MapMouseEvent) => {
    // Query features at click point
    const features = map.queryRenderedFeatures(e.point, {
      layers: [MAPBOX_CONFIG.countryBoundaries.fillLayerId],
    });

    // If no country was clicked, reset the filter
    if (features.length === 0) {
      onReset();
    }
  };

  // Add click listener to the map (not just the country layer)
  map.on("click", onMapClick);

  // Return cleanup function
  return () => {
    map.off("click", onMapClick);
  };
}

/**
 * Create and manage a tooltip that follows the mouse over countries
 * Shows country name and film count
 *
 * @param map - Mapbox GL map instance
 * @param countryCounts - Map of country names to film counts
 * @returns Cleanup function to remove tooltip
 */
export function setupCountryTooltip(map: mapboxgl.Map, countryCounts: Map<string, number>): () => void {
  // Helper to find film count for an ISO3 code
  const getFilmCount = (iso3Code: string): number => {
    for (const [name, count] of countryCounts) {
      const mappedIso3 = countryNameToISO3(name);
      if (mappedIso3 === iso3Code) {
        return count;
      }
    }
    return 0;
  };

  // Create tooltip element
  const tooltip = document.createElement("div");
  tooltip.className = "mapbox-country-tooltip";
  tooltip.style.cssText = `
        position: absolute;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 14px;
        pointer-events: none;
        z-index: 1000;
        display: none;
        border: 1px solid #F3B33B;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    `;
  map.getContainer().appendChild(tooltip);

  const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const iso2Code = feature.properties?.iso_3166_1 as string;

      if (!iso2Code) return;

      // Convert ISO2 → ISO3 → Country Name
      // Mapbox's iso_3166_1 property contains ISO2 codes (e.g., "US", "FR")
      const iso3Code = iso2ToISO3(iso2Code);
      const countryName = iso3Code ? iso3ToCountryName(iso3Code) : null;
      const displayName = countryName || (feature.properties?.name_en as string) || iso2Code;
      const filmCount = iso3Code ? getFilmCount(iso3Code) : 0;

      // Update tooltip content
      tooltip.innerHTML = `
                <div style="font-weight: 600; margin-bottom: 4px;">${displayName}</div>
                <div style="color: #F3B33B; font-size: 12px;">${filmCount} ${filmCount === 1 ? "film" : "films"}</div>
            `;

      // Position tooltip near cursor
      tooltip.style.left = `${e.point.x + 10}px`;
      tooltip.style.top = `${e.point.y + 10}px`;
      tooltip.style.display = "block";
    }
  };

  const onMouseLeave = () => {
    tooltip.style.display = "none";
  };

  // Add event listeners
  map.on("mousemove", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseMove);
  map.on("mouseleave", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseLeave);

  // Return cleanup function
  return () => {
    map.off("mousemove", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseMove);
    map.off("mouseleave", MAPBOX_CONFIG.countryBoundaries.fillLayerId, onMouseLeave);
    tooltip.remove();
  };
}
